/**
 * successCriteria.ts
 *
 * Zentrale, einzige Quelle der Erfolgs-Definition der Ruhestandssimulation.
 *
 * Verfeinerte Definition (Nutzer-Entscheidung, Re-Audit F7):
 * Ein Portfolio ist ERFOLGREICH, wenn
 *   (1) es das Zielalter erreicht (jahresergebnisse.length >= entnahmeJahre + 1) UND
 *   (2) im ZIELJAHR (letztem Eintrag) mindestens SUCCESS_WITHDRAWAL_THRESHOLD der
 *       inflationsadjustierten Zielentnahme tatsächlich bedient wurde
 *       (gesamtentnahme / zielentnahme >= Schwelle).
 *
 * Die Schwelle bezieht sich bewusst auf die ENTNAHME-Bedienung im Zieljahr, NICHT auf
 * den Endsaldo:
 *   - Volle Zieljahr-Entnahme + Endsaldo 0 € => Erfolg (100 % bedient).
 *   - Nur Teil-Entnahme im Zieljahr (Saldo lief leer) => Misserfolg, obwohl das
 *     Zielalter formal "erreicht" wurde ("Zieljahr unterfinanziert").
 *
 * Beide Strategien (1-Topf / 3-Töpfe) nutzen exakt dieselbe Funktion, damit der
 * Vergleich fair bleibt und Erfolgsrate, Failure-KPIs, Alters-Histogramm und Export
 * widerspruchsfrei sind.
 */

import { SIMULATION_CONFIG } from '../config/simulation';
import { BaseYearData } from '../types/simulation';

/**
 * Bedienungsgrad des Zieljahres: Anteil der tatsächlich bedienten Zielentnahme im
 * letzten Eintrag. Gibt `null` zurück, wenn das Zielalter NICHT erreicht wurde
 * (jahresergebnisse zu kurz) — der Aufrufer behandelt das als Misserfolg.
 */
export function getTargetYearFundingRatio(
  jahresergebnisse: Pick<BaseYearData, 'gesamtentnahme' | 'zielentnahme'>[] | undefined,
  entnahmeJahre: number
): number | null {
  if (!jahresergebnisse || jahresergebnisse.length < entnahmeJahre + 1) {
    return null; // Zielalter nicht erreicht
  }
  const last = jahresergebnisse[jahresergebnisse.length - 1];
  const ziel = last?.zielentnahme ?? 0;
  // Degenerierter Fall (keine Zielentnahme, z.B. Entnahmerate 0): Zieljahr gilt als
  // voll bedient, damit das Erreichen des Zielalters nicht künstlich als Failure zählt.
  if (ziel <= 0) {
    return 1;
  }
  return last.gesamtentnahme / ziel;
}

/**
 * Zentrales Erfolgskriterium — überall dort zu verwenden, wo bisher
 * `length >= entnahmeJahre + 1` als Erfolgsdefinition stand.
 */
export function isStrategySuccessful(
  jahresergebnisse: Pick<BaseYearData, 'gesamtentnahme' | 'zielentnahme'>[] | undefined,
  entnahmeJahre: number
): boolean {
  const ratio = getTargetYearFundingRatio(jahresergebnisse, entnahmeJahre);
  if (ratio === null) {
    return false;
  }
  return ratio >= SIMULATION_CONFIG.SUCCESS_WITHDRAWAL_THRESHOLD;
}

/**
 * True, wenn ein Lauf das Zielalter zwar ERREICHT, dort aber die 95 %-Schwelle reißt
 * ("Zieljahr unterfinanziert"). Dient der Failure-Klassifikation: solche Läufe sind
 * Failures mit Failure-Alter == Zielalter (nicht "früh erschöpft").
 */
export function isTargetYearUnderfunded(
  jahresergebnisse: Pick<BaseYearData, 'gesamtentnahme' | 'zielentnahme'>[] | undefined,
  entnahmeJahre: number
): boolean {
  const ratio = getTargetYearFundingRatio(jahresergebnisse, entnahmeJahre);
  if (ratio === null) {
    return false; // Zielalter gar nicht erreicht -> frühe Erschöpfung, kein Zieljahr-Fall
  }
  return ratio < SIMULATION_CONFIG.SUCCESS_WITHDRAWAL_THRESHOLD;
}
